import { call, cancelled, put, take, takeLatest } from "redux-saga/effects";
import {
  uploadProgress,
  uploadSuccess,
  uploadFailure,
} from "../slices/uploadSlice";
import UploadService from "../../services/UploadService";
import { eventChannel, END } from "redux-saga";

function createUploadProgressChannel({ file, type }) {
  return eventChannel((emitter) => {
    // Call the service with the file and progress callback
    UploadService.upload(file, type, (progress) => {
      emitter({ progress }); // Emit progress updates
    })
      .then((response) => {
        emitter({ success: true, data: response.data }); // Emit success when server returns 200 OK
        emitter(END); // Close the channel after success
      })
      .catch((error) => {
        // Keep the API's own answer: CustomResponseMiddleware rewrites every
        // 400 to { response: "<ErrorCode>", ... } (e.g. INVALID_IMAGE), and
        // losing it here meant every upload failure looked the same to the
        // store (status 400 "SOMETHING_ELSE_WRONG" except for a 413).
        emitter({
          error: error.message,
          status: error?.response?.status,
          errorData: error?.response?.data,
        });
        emitter(END); // Close the channel on error
      });

    // The subscriber must return an unsubscribe function
    return () => {};
  });
}

export function* handleFileUpload(action) {
  const file = action.payload.file.name;
  const progressChannel = yield call(
    createUploadProgressChannel,
    action.payload
  );

  try {
    while (true) {
      const ajax = yield take(progressChannel);
      const { progress, success, error, status, errorData, data } = ajax;

      if (progress !== undefined) {
        yield put(uploadProgress({ file, progress })); // Dispatch progress updates
      }

      if (success) {
        const response =
          typeof data === "string"
            ? JSON.parse(data.replaceAll('{"response":"please wait"}', ""))
            : data;

        yield put(uploadSuccess({ file, response: { ...response.response } })); // Dispatch success when the server returns 200 OK
        break; // Break out of the loop and close the channel
      }

      if (error) {
        // CustomResponseMiddleware rewrites every 400 to
        // { response: "<ErrorCode>", ... } (e.g. INVALID_IMAGE); keep that
        // code so the store can show its own message instead of a generic
        // one for every upload failure.
        const code =
          (typeof errorData?.response === "string" && errorData.response) ||
          null;
        yield put(
          uploadFailure({
            status: status || (error.includes("413") ? 413 : 400),
            file,
            response: code || "SOMETHING_ELSE_WRONG",
          })
        ); // Dispatch failure if there's an error
        break; // Break out of the loop and close the channel
      }
    }
  } catch (error) {
    yield put(
      uploadFailure({
        status: error?.response?.status || 400,
        file,
        response:
          (typeof error?.response?.data?.response === "string" &&
            error.response.data.response) ||
          "SOMETHING_ELSE_WRONG",
      })
    ); // Handle any other errors
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

export default function* watchUploadSaga() {
  yield takeLatest("uploads/uploadRequest", handleFileUpload);
}
